/**
 * Offline read cache.
 *
 * Phase 1 of offline support: the app may *show* the last fetched catalogue and
 * customer list while the network is down, and nothing else. Writes stay
 * online-only — a sale that cannot reach the server must fail visibly rather
 * than queue up silently, because a queued write needs idempotency keys and a
 * stock-conflict policy that do not exist yet.
 *
 * Design notes:
 * - A saved response expires after `MAX_CACHE_AGE_MS`. Stale product prices and
 *   stock counts are worse than an honest error, so old data is not served.
 * - Only the two read endpoints deliberately wrapped in `cachedRead` are
 *   stored. Auth, payments and sales are never cached.
 * - The customer directory is personal data at rest on the device, so the whole
 *   cache is wiped when the session ends (see `clearOfflineCache` callers).
 * - IndexedDB is unavailable in some private modes; every operation degrades to
 *   a no-op so the app behaves exactly as it did before this file existed.
 */

const DB_NAME = 'stockwise-offline';
const DB_VERSION = 1;
const STORE = 'responses';

/** How old a saved response may be before the UI refuses to show it. */
export const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * How long the sell screen's own data may be served: the product catalogue,
 * mixes and credit customers.
 *
 * Longer than the default on purpose. A shop offline for two days cannot simply
 * stop selling because the price list is a day old — it rings up at the last
 * known price and the banner says how old that is. Anything that is *not* needed
 * to complete a sale keeps the 24-hour rule.
 */
export const CATALOGUE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface CachedEntry<T> {
  data: T;
  /** When this response was received from the server (epoch ms). */
  savedAt: number;
}

export interface CacheStatus {
  /** True while the data on screen came from the cache instead of the server. */
  usingCache: boolean
  /** When the cached data now on screen was fetched. */
  cachedAt: number | null
}

// Whether the device can reach the API is owned by `@/lib/connectivity`; this
// module only tracks whether the data on screen came from the cache.
let status: CacheStatus = {
  usingCache: false,
  cachedAt: null,
};

const listeners = new Set<(next: CacheStatus) => void>();

function publish(patch: Partial<CacheStatus>): void {
  status = { ...status, ...patch };
  for (const listener of listeners) listener(status);
}

export function getCacheStatus(): CacheStatus {
  return status;
}

/** Subscribe to cache/network status. Returns an unsubscribe function. */
export function subscribeCacheStatus(listener: (next: CacheStatus) => void): () => void {
  listeners.add(listener);
  listener(status);
  return () => {
    listeners.delete(listener);
  };
}

/** A read came from the server: the data on screen is current again. */
export function markFresh(): void {
  if (!status.usingCache) return;
  publish({ usingCache: false, cachedAt: null });
}

/** A read fell back to the cache: tell the UI how old the data is. */
export function markStale(savedAt: number): void {
  publish({ usingCache: true, cachedAt: savedAt });
}

// ── IndexedDB plumbing ───────────────────────────────────────────────────────

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    // Any failure (private mode, quota, blocked upgrade) means "no cache".
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });

  return dbPromise;
}

async function runRequest<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  // Await the connection first: an async Promise executor would swallow any
  // rejection instead of surfacing it, and every path here must resolve to
  // "no cache" rather than reject into a caller's query.
  const db = await openDb();
  if (!db) return null;

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction(STORE, mode);
      const request = action(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export function readCache<T>(key: string): Promise<CachedEntry<T> | null> {
  return runRequest<CachedEntry<T>>('readonly', (store) => store.get(key) as IDBRequest<CachedEntry<T>>);
}

export async function writeCache<T>(key: string, data: T): Promise<void> {
  const entry: CachedEntry<T> = { data, savedAt: Date.now() };
  await runRequest('readwrite', (store) => store.put(entry, key));
}

/** Wipe everything. Called when the session ends, because the customer
 * directory cached here is personal data. */
export async function clearOfflineCache(): Promise<void> {
  await runRequest('readwrite', (store) => store.clear());
  publish({ usingCache: false, cachedAt: null });
}
